/**
 * DashboardLayout.jsx - Layout wrapper per tutte le dashboard
 * 100% Blueprint-driven - ZERO testi hardcoded
 * Header + Sidebar con navigazione + Footer
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import supabase from '../../lib/supabase';
import Header from '../Header';
import Footer from '../Footer';
import { 
  Home,
  FolderKanban, 
  Layers, 
  Search,
  Sparkles,
  Bookmark,
  Users,
  Zap,
  LayoutDashboard,
  Menu,
  X,
  Settings,
  HelpCircle,
  ChevronDown,
  LogOut,
  Building2,
  FileText
} from 'lucide-react';

// Icon map for dynamic rendering
const iconMap = { 
  Home, FolderKanban, Layers, Search, Sparkles, Bookmark, 
  Users, Settings, HelpCircle, ChevronDown, LogOut, Zap, Building2, LayoutDashboard, FileText 
};
const getIcon = (name) => iconMap[name] || Layers;

// Default config (fallback if Blueprint not configured)
const DEFAULT_CONFIG = {
  sidebar: {
    logo: {
      text: 'SR',
      brand: 'SLABREALITY'
    },
    navigation: [
      { icon: 'Home', label: 'Dashboard', path: '/dashboard' },
      { icon: 'FolderKanban', label: 'Progetti', path: '/projects' },
      { icon: 'Layers', label: 'Materiali', path: '/materials' },
      { icon: 'Search', label: 'Visual Match', path: '/match' },
      { icon: 'Sparkles', label: 'SRE Engine', path: '/sre' },
      { icon: 'FileText', label: 'Proposte', path: '/proposals' },
      { icon: 'Bookmark', label: 'Salvati', path: '/workspace' },
    ],
    upgrade_card: {
      enabled: true,
      headline: 'Sblocca il potenziale',
      subheadline: 'di SlabReality',
      description: 'Passa a un piano superiore per accedere a strumenti avanzati, più progetti e funzionalità team.',
      cta: 'Upgrade',
      route: '/pricing'
    },
    user_menu: {
      settings_label: 'Impostazioni',
      help_label: 'Aiuto & Supporto',
      logout_label: 'Esci'
    }
  }
};

const DashboardLayout = ({ children }) => {
  const { user, profile, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [upgradeCard, setUpgradeCard] = useState(null);
  const [branding, setBranding] = useState({ logo_text: 'SR', site_name: 'SLABREALITY' });
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // Load sidebar items and branding from Blueprint CMS
  useEffect(() => {
    const loadSidebarConfig = async () => {
      try {
        // Load sidebar items
        const { data: itemsData, error: itemsError } = await supabase
          .from('blueprint_sidebar_items')
          .select('*')
          .eq('is_active', true)
          .order('order_index', { ascending: true });
        
        if (!itemsError && itemsData && itemsData.length > 0) {
          console.log('📋 Sidebar items loaded from Blueprint:', itemsData.length);
          setSidebarItems(itemsData);
        } else {
          // Fallback defaults
          console.log('📋 Using default sidebar items');
          setSidebarItems([
            { slug: 'dashboard', label_it: 'Dashboard', path: '/dashboard', icon: 'Home' },
            { slug: 'projects', label_it: 'Progetti', path: '/projects', icon: 'FolderKanban' },
            { slug: 'materials', label_it: 'Materiali', path: '/materials', icon: 'Layers' },
            { slug: 'visual-match', label_it: 'Visual Match', path: '/match', icon: 'Search' },
            { slug: 'sre-engine', label_it: 'SRE Engine', path: '/sre', icon: 'Sparkles' },
            { slug: 'workspace', label_it: 'Salvati', path: '/workspace', icon: 'Bookmark' }
          ]);
        }
        
        // Load branding
        const { data: brandingData } = await supabase
          .from('blueprint_config')
          .select('branding')
          .eq('is_active', true)
          .single();
        
        if (brandingData?.branding) {
          setBranding({
            logo_text: brandingData.branding.logo_text || 'SR',
            site_name: brandingData.branding.site_name || 'SLABREALITY'
          });
        }
        
        // Load upgrade card from dashboard config
        const { data: configData } = await supabase
          .from('blueprint_dashboard_config')
          .select('sidebar_config')
          .eq('role_professional', profile?.role_professional || 'default')
          .eq('lang', 'it')
          .eq('is_active', true)
          .single();
        
        if (configData?.sidebar_config?.upgrade_card) {
          setUpgradeCard(configData.sidebar_config.upgrade_card);
        } else {
          // Default upgrade card
          setUpgradeCard({
            enabled: true,
            headline: 'Sblocca il potenziale',
            subheadline: 'di SlabReality',
            description: 'Passa a un piano superiore per accedere a strumenti avanzati.',
            cta: 'Upgrade',
            route: '/pricing'
          });
        }
      } catch (err) {
        console.error('📋 Sidebar config error:', err);
      } finally {
        setConfigLoaded(true);
      }
    };
    
    loadSidebarConfig();
  }, [profile?.role_professional]);
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Header */}
      <Header />
      
      {/* Main Container with Sidebar */}
      <div className="flex flex-1 pt-20">
        {/* Mobile Sidebar Toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#B08B5C] rounded-full flex items-center justify-center shadow-lg"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        
        {/* Sidebar Overlay for Mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <aside className={`
          w-64 bg-[#0a0a0a] border-r border-white/5 flex flex-col
          fixed lg:sticky top-20 h-[calc(100vh-5rem)] z-40
          transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          {/* Logo in Sidebar */}
          <div className="p-6 pb-4 border-b border-white/5">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#b08b5c] to-[#8a6d46] flex items-center justify-center">
                  <span className="text-white font-serif text-lg italic">{branding.logo_text}</span>
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] tracking-[0.3em] text-white/40 font-light">
                  {branding.site_name}
                </span>
              </div>
            </Link>
          </div>
          
          {/* Navigation - Dynamic from Blueprint CMS */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {sidebarItems.map((item) => {
              // Check role visibility
              if (item.visible_to_roles && item.visible_to_roles !== 'all') {
                const allowedRoles = item.visible_to_roles.split(',').map(r => r.trim());
                if (!allowedRoles.includes(profile?.role_professional)) return null;
              }
              
              // Check admin requirement
              if (item.requires_admin && !isAdmin) return null;
              
              const Icon = getIcon(item.icon);
              return (
                <Link
                  key={item.slug}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path) 
                      ? 'bg-[#B08B5C]/20 text-[#B08B5C]' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.slug}`}
                >
                  <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
                  <span className="font-light">{item.label_it}</span>
                  {item.show_badge && item.badge_type === 'new' && (
                    <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-[#B08B5C] text-black rounded-full font-medium">
                      NEW
                    </span>
                  )}
                  {item.show_badge && item.badge_type === 'pro' && (
                    <span className="ml-auto px-1.5 py-0.5 text-[10px] bg-gradient-to-r from-amber-500 to-yellow-500 text-black rounded-full font-medium">
                      PRO
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          
          {/* Upgrade Card - Only pricing link, no user section */}
          {upgradeCard?.enabled && (
            <div className="px-3 mb-4 mt-auto">
              <div className="p-4 border border-[#B08B5C]/20 rounded-xl bg-gradient-to-br from-[#b08b5c]/5 to-transparent">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-[#b08b5c]" />
                  <p className="text-xs text-[#b08b5c] font-medium">{upgradeCard.headline}</p>
                </div>
                {upgradeCard.subheadline && (
                  <p className="text-xs text-white/60 mb-1">{upgradeCard.subheadline}</p>
                )}
                {upgradeCard.description && (
                  <p className="text-[10px] text-white/40 mb-3 leading-relaxed">
                    {upgradeCard.description}
                  </p>
                )}
                <Link 
                  to={upgradeCard.route || '/pricing'} 
                  className="w-full bg-[#B08B5C] text-black text-xs py-2 block text-center rounded-lg font-medium hover:bg-[#C9A46E] transition-colors"
                  data-testid="upgrade-btn"
                >
                  {upgradeCard.cta}
                </Link>
              </div>
            </div>
          )}
        </aside>
        
        {/* Main Content */}
        <main className="flex-1 min-h-[calc(100vh-5rem)]">
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default DashboardLayout;
